<template>
  <div class="dynamic-accordion">
    <div
      v-for="(panel, index) of panels"
      :key="index"
      class="dynamic-accordion__panel"
    >
      <button
        class="dynamic-accordion__toggle"
        :class="{ [$class('active')]: isActive(index) }"
        :aria-expanded="isActive(index)"
        type="button"
        @click="togglePanel(index)"
      >
        <component
          :is="headingType"
          class="dynamic-accordion__heading"
          :class="panel.classes?.title"
          v-text="panel.title"
        />

        <component :is="isActive(index) ? 'icon-minus' : 'icon-plus'" />
      </button>

      <transition-height>
        <div
          v-if="isActive(index)"
          class="dynamic-accordion__content"
          :class="panel.classes?.content"
          v-html="panel.content"
        />
      </transition-height>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Dynamic Accordion (dynamic-accordion)
 * -----------------------------------------------------------------------------
 * Renders a set of dynamic-accordion panels that can be opened and closed.
 *
 * @param {String} [headingType] - Element type for the toggle label.
 * @param {Array} panelData - Array of panel objects.
 * @param {String} panelData.content - Panel content.
 * @param {String} panelData.title - Title of the panel, used as a label for the
 * toggle button.
 * @param {Object} panelData.classes - Additional classes to use.
 */
import { mapState } from 'vuex'

import TransitionHeight from '~global/transition-height/transition-height'

import IconMinus from '~icons/misc/minus.svg'
import IconPlus from '~icons/misc/plus.svg'

export default {
  name: 'DynamicAccordion',

  components: {
    IconMinus,
    IconPlus,
    TransitionHeight,
  },

  props: {
    headingType: {
      type: String,
      default: 'span',
    },
    panelData: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      panels: [],
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),
  },

  mounted() {

    /**
     * Store panels on the component with a toggleable 'active' property.
     * - Also adds default title class.
     */
    this.panels = this.panelData.map((panel) => {
      return {
        active: false,
        classes: {
          title: 'text-heading-2xs',
        },
        ...panel,
      }
    })
  },

  methods: {

    /**
     * Check if a panel is active using its index.
     * @param {Number} index - Index of a specific panel.
     * @returns {Boolean}
     */
    isActive(index) {
      return this.panels[index].active
    },

    /**
     * Toggle a panel's active state.
     * - On mobile viewports, panels are toggled independently.
     * - On non-mobile viewports, opening one panel will close all other panels.
     * @param {Number} targetPanel - Index of a specific panel.
     */
    togglePanel(targetPanelIndex) {
      if (this.isMobile) {
        const targetPanel = this.panels[targetPanelIndex]
        targetPanel.active = !targetPanel.active
        return
      }

      if (this.isActive(targetPanelIndex)) {
        this.panels[targetPanelIndex].active = false
        return
      }

      this.panels.forEach((panel, index) => {
        panel.active = targetPanelIndex === index
      })
    },
  },
}
</script>

<style lang="scss">
@import './dynamic-accordion';
</style>
